'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import type { DropdownPosition } from '../../types/header';
import type { NavItem } from '../../types/navigation';

const VIEWPORT_PADDING = 16;
const MIN_DROPDOWN_VIEWPORT_WIDTH = 280;
const SIMPLE_DROPDOWN_MAX_WIDTH = 320;
const MEGA_MENU_MAX_WIDTH = 560;
const MEGA_MENU_MIN_WIDTH = 500;
const SIMPLE_DROPDOWN_MIN_WIDTH = 236;
const SIMPLE_DROPDOWN_EXTRA_WIDTH = 36;
const DROPDOWN_OFFSET_TOP = 8;
const DROPDOWN_CLOSE_DELAY_MS = 130;

const useHeaderDropdown = (navItems: NavItem[]) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navTriggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const clearCloseTimer = useCallback(() => {
    if (!closeTimer.current) {
      return;
    }

    clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);

  const closeDropdown = useCallback(() => {
    clearCloseTimer();
    setOpenDropdown(null);
  }, [clearCloseTimer]);

  const syncDropdownPosition = useCallback(
    (key: string) => {
      const trigger = navTriggerRefs.current[key];

      if (!trigger || typeof window === 'undefined') {
        return;
      }

      const navItem = navItems.find((item) => item.label === key);
      const triggerRect = trigger.getBoundingClientRect();
      const maxWidth = Math.max(
        MIN_DROPDOWN_VIEWPORT_WIDTH,
        window.innerWidth - VIEWPORT_PADDING * 2,
      );
      const isMegaMenu = Boolean(navItem?.megaMenu);
      const preferredWidth = isMegaMenu
        ? Math.min(MEGA_MENU_MAX_WIDTH, maxWidth)
        : Math.min(SIMPLE_DROPDOWN_MAX_WIDTH, maxWidth);
      const minWidth = isMegaMenu
        ? Math.min(MEGA_MENU_MIN_WIDTH, preferredWidth)
        : Math.max(SIMPLE_DROPDOWN_MIN_WIDTH, triggerRect.width + SIMPLE_DROPDOWN_EXTRA_WIDTH);
      const triggerCenter = triggerRect.left + triggerRect.width / 2;
      const leftAnchor = isMegaMenu ? triggerCenter - preferredWidth / 2 : triggerRect.left;
      const left = Math.min(
        Math.max(VIEWPORT_PADDING, leftAnchor),
        window.innerWidth - preferredWidth - VIEWPORT_PADDING,
      );

      setDropdownPosition({
        left,
        maxWidth,
        minWidth: Math.min(minWidth, maxWidth),
        top: triggerRect.bottom + DROPDOWN_OFFSET_TOP,
        width: isMegaMenu ? preferredWidth : undefined,
      });
    },
    [navItems],
  );

  const openDropdownMenu = useCallback(
    (key: string) => {
      clearCloseTimer();
      syncDropdownPosition(key);
      setOpenDropdown(key);
    },
    [clearCloseTimer, syncDropdownPosition],
  );

  const scheduleDropdownClose = useCallback(() => {
    clearCloseTimer();
    closeTimer.current = setTimeout(closeDropdown, DROPDOWN_CLOSE_DELAY_MS);
  }, [clearCloseTimer, closeDropdown]);

  const setNavTriggerRef = useCallback((key: string, element: HTMLButtonElement | null) => {
    navTriggerRefs.current[key] = element;
  }, []);

  useEffect(() => {
    if (!openDropdown) {
      return undefined;
    }

    const syncPosition = () => syncDropdownPosition(openDropdown);
    syncPosition();

    window.addEventListener('resize', syncPosition);
    window.addEventListener('scroll', syncPosition, true);

    return () => {
      window.removeEventListener('resize', syncPosition);
      window.removeEventListener('scroll', syncPosition, true);
    };
  }, [openDropdown, syncDropdownPosition]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  return {
    closeDropdown,
    dropdownPosition,
    dropdownRef,
    openDropdown,
    openDropdownMenu,
    scheduleDropdownClose,
    setNavTriggerRef,
  };
};

export default useHeaderDropdown;
